import { Note } from '@propertypal/shared/src/types/notes';
import { Property } from '@propertypal/shared/src/types/property';
import PropertyNotFound from '@propertypal/web-ui/src/property/PropertyNotFound';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropertyDetails from '../components/property/PropertyDetails';
import { addRecentProperty } from '../components/search/RecentlyViewed';
import useGpt from '../hooks/useGptSlot';
import { setTargeting } from '../services/ads/googletag';
import getProperty from '../services/property';
import { getPropertyHeatingType, getPropertyPriceParam } from '../utils/ad-utils';
import { requireAuthToken } from '../utils/auth';
import buildPropertySchema from '../utils/propertySchema';

interface Props {
  published: boolean;
  property: Property;
  loggedIn: boolean;
}

export const getServerSideProps = requireAuthToken(async (ctx, store) => {
  const id = ctx.query.id as string;
  const address = ctx.query.address as string;
  const currency = typeof ctx.query.currency === 'string' ? ctx.query.currency : undefined;
  const [result, published] = await getProperty(id, store.dispatch, ctx, currency);

  if (result.data.path && (!address || `/${address}/${id}` !== result.data.path)) {
    const params = ctx.resolvedUrl && ctx.resolvedUrl.split('?')[1];
    const paramsStr = params ? `?${params}` : '';

    return {
      redirect: {
        destination: `${result.data.path}${paramsStr}`,
        permanent: true,
      },
    };
  }

  ctx.res.statusCode = published ? 200 : 404;

  // Allow iframes on featured developments (fermacproperties.com)
  // By default 'X-Frame-Options' is set to `DENY` in `next.config.js`
  if (result?.data?.featuredDevelopmentContent) ctx.res.removeHeader('X-Frame-Options');

  return {
    props: {
      published,
      property: result.data,
    },
  };
});

const PropertyPage: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [property, setProperty] = useState<Property>(props.property);
  const [notes, setNotes] = useState<Note[]>(property.notes || []);

  const updateProperty = async () => {
    if (typeof router.query.id === 'string') {
      const currency = typeof router.query.currency === 'string' ? router.query.currency : undefined;
      const [result] = await getProperty(router.query.id, dispatch, undefined, currency);

      setProperty(result.data);
      setNotes(result.data.notes || []);
    }
  };

  useEffect(() => {
    // next.js has bug where getServersideProps will not always be called
    // https://github.com/vercel/next.js/issues/34365
    // The following logic checks for a bad route and manually re-fetches the property details
    // checking props.property.pathId detects if getServersideProps has not run
    // checking property.pathId ensures this is not repeatedly called when navigating through gallery
    if (!router.asPath.includes(props.property.pathId) && !router.asPath.includes(property.pathId)) {
      updateProperty();
    }
  }, [router.asPath]);

  useEffect(() => {
    setProperty(props.property);
    setNotes(props.property.notes || []);
    addRecentProperty(props.property);
  }, [props.property]);

  useGpt(() => {
    setTargeting({
      PropertyID: property?.id,
      AccountNumber: property?.account?.accountNumber,
      Category: property?.propertyType?.key,
      SaleType: property?.saleType?.key,
      Location: [property?.postcode?.split(' ')[0]],
      EPCActualBand: property?.epc?.ratingShorthand?.split('/')[0]?.substring(0, 1),
      EPCPotentialBand: property?.epc?.ratingShorthand?.split('/')[1]?.substring(0, 1),
      BER: property?.ber?.alphanumericRating,
      Price: getPropertyPriceParam(property),
      HeatingType: getPropertyHeatingType(property),
      LoggedIn: props.loggedIn ? 'yes' : 'no',
    });
  }, [property?.id]);

  if (!props.published) {
    return <PropertyNotFound property={property} loggedIn={props.loggedIn} />;
  }

  const structuredData = buildPropertySchema(property);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>

      <PropertyDetails
        key={property.id}
        property={property}
        loggedIn={props.loggedIn}
        notes={notes}
        setNotes={setNotes}
      />
    </>
  );
};

export default React.memo(PropertyPage);
